import type { BuildingBounds } from "building-types/building/buildingBounds";
import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { ExteriorMaterialOverride } from "building-types/materials/materialTypes";
import type { GarageSettings } from "building-types/settings/garageSettings";
import { ContextHomeProp } from "building-types/site/ContextHomeProp";
import type { GeneratedSite, Site } from "building-types/site/site";
import type { Table } from "common/data/table";
import { exteriorMaterialOverrideNew } from "doc/utils/materialTools";
import { generatedSiteNew } from "doc/utils/siteTools";
import { faceByFourPoints, faceByThreePoints } from "geom/brep";
import { ONE_MINUS_DIST_EPSILON } from "geom/core";
import { extrudeFaceAlongPolyLine3 } from "geom/extrude";
import GeomTagGroup from "geom/geomTagGroup";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import {
  polyLine2ByTwoPoints,
  polyLine2Extend,
  polyLine2Length,
  polyLine2Move,
  polyLine2ReflectOverYAxis,
  polyLine2SetCW,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { polyLine3Copy, polyLine3Move, polyLine3New, polyLine3Shatter } from "geom/polyLine3";
import { rect2ByTwoPoints } from "geom/rect2";
import { makeFence } from "./fence";
import { sitePropDefaultValues } from "./propSite";
import type { SiteZGeom } from "./siteUtils";
import { getSiteDoorData, getSiteZ, SiteDoorType } from "./siteUtils";
import { makeSidewalk } from "./siteUtils";

function makeCurbProfile(
  width: number,
  height: number,
  gutterWidth: number,
  gutterHeight: number,
  flip: boolean = false,
): PolyLine2 {
  const curbPl = polyLine2New([
    0,
    0,
    -(width + gutterWidth),
    0,
    -(width + gutterWidth),
    gutterHeight,
    -width,
    gutterHeight,
    -width,
    height,
    0,
    height,
    0,
    0,
  ]);

  if (flip) {
    polyLine2ReflectOverYAxis(curbPl);
    polyLine2SetCW(curbPl);
  }

  return curbPl;
}
interface MakeDynamicSiteOptions {
  readonly site: Site;
  readonly doorPlacements: Table<GeneratedDoorPlacement>;
  readonly buildingBounds: BuildingBounds;
  readonly planarGraphs: Table<PolygonPlanarGraph>;
  readonly garageSettings: GarageSettings;
  readonly levels: Table<Level>;
}

export function makeDynamicSite(options: MakeDynamicSiteOptions): GeneratedSite | undefined {
  const { site, doorPlacements, buildingBounds, planarGraphs, garageSettings, levels } = options;

  const {
    frontSetback = 0,
    sideSetback = 0,
    backSetback = 0,
    sidewalkWidth = 0,
    grassCurbWidth = 0,
    curbHeight = 0,
    sidewalkRampLength = 0,
    fence,
    fenceHeight = 0,
    fenceSetback = 0,
    backRoad,
    roadWidth = 0,
    roadVerticalOffset = 0,
    homeBackVerticalOffset = 0,
    homeFrontVerticalOffset = 0,
    sidewalk,
    curb,
    frontWalk,
    frontWalkWidth = 0,
    frontWalkConnectedToDriveway,
    frontWalkConnectedSetback = 0,
    driveway,
    contextHomes,
  } = site;

  // approximate a dynamic site
  const ret = generatedSiteNew(site);

  if (!buildingBounds?.aabb) {
    return undefined;
  }

  const { minY: houseFront, maxY: houseBack, minX: houseLeft, maxX: houseRight } = buildingBounds.aabb;
  const houseLowPoint = buildingBounds.buildingBottomElevation;

  const garageDoorData = getSiteDoorData(levels, planarGraphs, doorPlacements, SiteDoorType.GARAGE);
  if (!garageDoorData) {
    return undefined;
  }

  const { doorElevation: garageElevation, drivewayInfo } = garageDoorData;

  const entryDoorData = getSiteDoorData(levels, planarGraphs, doorPlacements, SiteDoorType.ENTRY);
  if (!entryDoorData) {
    return undefined;
  }

  const { doorElevation: frontDoorElevation, drivewayInfo: frontDoorInfo } = entryDoorData;

  const houseDepth = buildingBounds.aabb.maxY - buildingBounds.aabb.minY;
  const siteBack = houseBack + backSetback + 1;

  // Generate simplified topography
  const contextExtension = 2400;

  const siteContextLeft = -contextExtension;
  const siteContextRight = contextExtension;

  // set the front boundary to take the front of the house and extend it by the front setback
  const backBoundary = buildingBounds.aabb.maxY + backSetback;
  const frontBoundary = buildingBounds.aabb.minY - frontSetback;

  // define the back of the home and adjust the low point to clear the foundation.
  const siteAdjustZ = 12;
  const curbWidth = 18;
  const siteBackVerticalOffset = houseLowPoint - siteAdjustZ + homeBackVerticalOffset;

  // make the Topography
  const leftTopoRail = polyLine3New([
    siteContextLeft,
    -contextExtension,
    garageElevation + roadVerticalOffset,

    siteContextLeft,
    frontBoundary + sidewalkWidth + grassCurbWidth,
    garageElevation + roadVerticalOffset,

    siteContextLeft,
    houseFront,
    garageElevation + homeFrontVerticalOffset,

    siteContextLeft,
    frontBoundary + houseDepth + frontSetback,
    siteBackVerticalOffset,

    siteContextLeft,
    siteBack,
    siteBackVerticalOffset,

    siteContextLeft,
    contextExtension,
    siteBackVerticalOffset,
  ]);

  const topoProfile = polyLine2New([0, -2, siteContextRight - siteContextLeft, -2]);

  // more complex topography controls are needed
  const topoSrfGeom = extrudeFaceAlongPolyLine3([topoProfile], leftTopoRail, 0, 0, 1);
  const topoSrf: SiteZGeom = {
    geometry: topoSrfGeom,
  };
  ret.topographyGeom.add(topoSrfGeom, exteriorMaterialOverrideNew("grass0"));

  // initialize some variables that will be set based on analysis
  let driveStartX = -1;
  let driveEndX = -1;

  // define sides of site, assumes home is centered on the site boundary and the site boundary is a rectangle
  // need ability to set the origin of the home relative to site and alignment
  let siteBoundaryLeft = houseLeft - sideSetback;
  let siteBoundaryRight = houseRight + sideSetback;
  let sideFacingGarage = false;
  let frontDrivewayRoadConnected = true;
  let backFacingGarage = false;

  if (driveway) {
    for (const info of drivewayInfo) {
      const { normalX, normalY, startX, startY, endX, endY } = info;
      const doorLine = polyLine2ByTwoPoints(startX, startY, endX, endY);
      polyLine2Extend(doorLine, garageSettings.drivewayDoorOffset, garageSettings.drivewayDoorOffset);

      const drivewayLeftRail = polyLine3New([
        doorLine[0],
        frontBoundary,
        garageElevation + roadVerticalOffset,
        doorLine[0],
        frontBoundary + sidewalkWidth + grassCurbWidth,
        garageElevation + roadVerticalOffset,
        doorLine[0],
        houseFront,
        garageElevation + homeFrontVerticalOffset,
        doorLine[0],
        doorLine[1],
        garageElevation,
      ]);

      if (driveStartX === -1) {
        driveStartX = doorLine[0];
      } else if (startX < driveStartX) {
        driveStartX = doorLine[0];
      }

      if (driveEndX === -1) {
        driveEndX = doorLine[2];
      } else if (driveEndX < endX) {
        driveEndX = doorLine[2];
      }

      const drivewayWidth = Math.min(polyLine2Length(doorLine), 220);

      if (normalY > ONE_MINUS_DIST_EPSILON) {
        // back facing!
        backFacingGarage = true;
        frontDrivewayRoadConnected = false;

        //
        const backDrivewayLeftRail = polyLine3New([
          doorLine[0],
          backBoundary + grassCurbWidth + sidewalkWidth,
          garageElevation + homeBackVerticalOffset,
          doorLine[0],
          backBoundary - sidewalkWidth - grassCurbWidth,
          garageElevation + homeBackVerticalOffset,
          doorLine[0],
          houseBack,
          garageElevation + homeBackVerticalOffset,
          doorLine[0],
          doorLine[1],
          garageElevation,
        ]);

        const drivewayProfile = rect2ByTwoPoints(0, -24, -(doorLine[2] - doorLine[0]), 0);
        polyLine2SetCW(drivewayProfile);
        ret.drivewayGeom.push({
          group: new GeomTagGroup<ExteriorMaterialOverride>().add(
            extrudeFaceAlongPolyLine3([drivewayProfile], backDrivewayLeftRail, 0, 0, 1),
            exteriorMaterialOverrideNew(site.drivewayMaterial ?? "driveway0"),
          ),
          pickHandle: undefined,
        });
      }

      if (Math.abs(normalY) > ONE_MINUS_DIST_EPSILON && !backFacingGarage) {
        // front facing
        const drivewayProfile = rect2ByTwoPoints(0, -24, doorLine[2] - doorLine[0], 0);
        polyLine2SetCW(drivewayProfile);
        ret.drivewayGeom.push({
          group: new GeomTagGroup<ExteriorMaterialOverride>().add(
            extrudeFaceAlongPolyLine3([drivewayProfile], drivewayLeftRail, 0, 0, 1),
            exteriorMaterialOverrideNew(site.drivewayMaterial ?? "driveway0"),
          ),
          pickHandle: undefined,
        });
      }

      const sideMargin = garageSettings.sideDrivewayMargin;

      if (Math.abs(normalX) > ONE_MINUS_DIST_EPSILON) {
        sideFacingGarage = true;
        if (normalX < 0) {
          // left facing
          siteBoundaryLeft -= drivewayWidth;
          driveStartX = doorLine[0] - sideMargin - drivewayWidth;
          driveEndX = doorLine[0] - sideMargin;
        } else {
          // right facing
          siteBoundaryRight += drivewayWidth;
          driveStartX = doorLine[0] + sideMargin;
          driveEndX = doorLine[0] + sideMargin + drivewayWidth;
        }

        let sideDriveOffset = doorLine[0] + sideMargin;
        if (normalX < 0) {
          sideDriveOffset = doorLine[2] - sideMargin - drivewayWidth;
        }

        const drivewayLeftRail = polyLine3New([
          sideDriveOffset,
          frontBoundary,
          garageElevation + roadVerticalOffset,

          sideDriveOffset,
          frontBoundary + sidewalkWidth + grassCurbWidth,
          garageElevation + roadVerticalOffset,

          sideDriveOffset,
          houseFront,
          garageElevation + homeFrontVerticalOffset,

          sideDriveOffset,
          doorLine[1],
          garageElevation,

          doorLine[0],
          doorLine[1],
          garageElevation,
        ]);

        const drivewayProfile = rect2ByTwoPoints(0, -6, drivewayWidth, 2);
        polyLine2SetCW(drivewayProfile);
        ret.drivewayGeom.push({
          group: new GeomTagGroup<ExteriorMaterialOverride>().add(
            extrudeFaceAlongPolyLine3([drivewayProfile], drivewayLeftRail, 0, 0, 1),
            exteriorMaterialOverrideNew(site.drivewayMaterial ?? "driveway0"),
          ),
          pickHandle: undefined,
        });
      }
    }
  }

  if (frontWalk) {
    for (const info of frontDoorInfo) {
      const { normalX, normalY, startX, startY, endX, endY } = info;

      if (Math.abs(normalX) > ONE_MINUS_DIST_EPSILON) {
        // side facing
        continue;
      }

      const doorLine = polyLine2ByTwoPoints(startX, startY, endX, endY);
      const doorMidX = (doorLine[0] + doorLine[2]) / 2;

      if (!frontWalkConnectedToDriveway || !frontDrivewayRoadConnected) {
        const frontWalkLeftRail = polyLine3New([
          doorMidX,
          doorLine[1],
          frontDoorElevation - 6,

          doorMidX,
          doorLine[1] - frontWalkWidth,
          frontDoorElevation - 6,

          doorMidX,
          houseFront,
          garageElevation + homeFrontVerticalOffset,

          doorMidX,
          frontBoundary + sidewalkWidth + grassCurbWidth,
          garageElevation + roadVerticalOffset,
        ]);

        if (Math.abs(normalY) > ONE_MINUS_DIST_EPSILON) {
          const frontWalkProfile = rect2ByTwoPoints(-frontWalkWidth / 2, -24, frontWalkWidth / 2, 2);
          polyLine2SetCW(frontWalkProfile);
          ret.sidewalkGeom.push({
            group: new GeomTagGroup<ExteriorMaterialOverride>().add(
              extrudeFaceAlongPolyLine3([frontWalkProfile], frontWalkLeftRail, 0, 0, 1),
              exteriorMaterialOverrideNew("driveway0"),
            ),
            pickHandle: undefined,
          });
        }
      } else {
        const frontWalkLeftRail = polyLine3New([
          doorMidX,
          doorLine[1],
          frontDoorElevation - 6,

          doorMidX,
          houseFront,
          getSiteZ(topoSrf, doorLine[0], houseFront),

          doorMidX,
          houseFront - frontWalkConnectedSetback,
          getSiteZ(topoSrf, doorLine[0], houseFront - frontWalkConnectedSetback),

          driveStartX > doorMidX ? driveStartX : driveEndX,
          houseFront - frontWalkConnectedSetback,
          getSiteZ(topoSrf, driveStartX, houseFront - frontWalkConnectedSetback),
        ]);

        if (Math.abs(normalY) > ONE_MINUS_DIST_EPSILON) {
          const frontWalkProfile = rect2ByTwoPoints(-frontWalkWidth / 2, -24, frontWalkWidth / 2, 1);
          polyLine2SetCW(frontWalkProfile);
          ret.sidewalkGeom.push({
            group: new GeomTagGroup<ExteriorMaterialOverride>().add(
              extrudeFaceAlongPolyLine3([frontWalkProfile], frontWalkLeftRail, 0, 0, 1),
              exteriorMaterialOverrideNew("driveway0"),
            ),
            pickHandle: undefined,
          });
        }
      }
    }
  }

  const leftSetbackRail = polyLine3Copy(leftTopoRail);
  const rightSetbackRail = polyLine3Copy(leftTopoRail);
  polyLine3Move(leftSetbackRail, -siteContextLeft + siteBoundaryLeft, 0, 0);
  polyLine3Move(rightSetbackRail, -siteContextLeft + siteBoundaryRight, 0, 0);

  // slice up the rails to get the site outline
  ret.setbackDatum.push(leftSetbackRail.slice(3, 15));
  ret.setbackDatum.push(rightSetbackRail.slice(3, 15));

  const backSetbackPl = polyLine3New([
    houseLeft - sideSetback,
    siteBack,
    siteBackVerticalOffset - 6,

    houseRight + sideSetback,
    siteBack,
    siteBackVerticalOffset - 6,
  ]);

  ret.setbackDatum.push(backSetbackPl);

  const curbBaseProfile = rect2ByTwoPoints(0, 0, 24, 0.5);
  polyLine2SetCW(curbBaseProfile);
  polyLine2Move(curbBaseProfile, 0, 0);

  const sidewalkRailFull = polyLine3New([
    siteContextLeft,
    frontBoundary,
    garageElevation + roadVerticalOffset,
    siteContextRight,
    frontBoundary,
    garageElevation + roadVerticalOffset,
  ]);

  const roadProfile = rect2ByTwoPoints(0, 0, roadWidth, 1);
  ret.roadGeom.push({
    group: new GeomTagGroup<ExteriorMaterialOverride>().add(
      extrudeFaceAlongPolyLine3([roadProfile], sidewalkRailFull, 0, 0, 1),
      exteriorMaterialOverrideNew("road0"),
    ),
    pickHandle: undefined,
  });

  if (backRoad) {
    const roadProfile = rect2ByTwoPoints(0, 0, -roadWidth, 1);
    const backRoadProfile = polyLine2Move(
      roadProfile,
      frontBoundary - backBoundary - (grassCurbWidth + sidewalkWidth),
      12 + homeBackVerticalOffset,
    );
    ret.roadGeom.push({
      group: new GeomTagGroup<ExteriorMaterialOverride>().add(
        extrudeFaceAlongPolyLine3([backRoadProfile], sidewalkRailFull, 0, 0, 1),
        exteriorMaterialOverrideNew("driveway0"),
      ),
      pickHandle: undefined,
    });
  }

  if (sidewalk || curb) {
    const sidewalkRail1 = polyLine3New([
      siteContextLeft,
      frontBoundary,
      garageElevation + roadVerticalOffset,
      driveStartX - sidewalkRampLength,
      frontBoundary,
      garageElevation + roadVerticalOffset,
    ]);

    const sidewalkRail2 = polyLine3New([
      driveEndX + sidewalkRampLength,
      frontBoundary,
      garageElevation + roadVerticalOffset,
      siteContextRight,
      frontBoundary,
      garageElevation + roadVerticalOffset,
    ]);

    if (curb) {
      const curbProfile = makeCurbProfile(6, curbHeight, curbWidth, 0.5, true);
      const curb1 = extrudeFaceAlongPolyLine3([curbProfile], sidewalkRail1, 0, 0, 1);
      const curb2 = extrudeFaceAlongPolyLine3([curbProfile], sidewalkRail2, 0, 0, 1);
      const curbBase = extrudeFaceAlongPolyLine3([curbBaseProfile], sidewalkRailFull, 0, 0, 1);

      const curbLeftRampFace = faceByFourPoints(
        driveStartX - sidewalkRampLength,
        frontBoundary,
        garageElevation + curbHeight + roadVerticalOffset,

        driveStartX - sidewalkRampLength,
        frontBoundary - 6,
        garageElevation + roadVerticalOffset + curbHeight,

        driveStartX,
        frontBoundary - 6,
        garageElevation + roadVerticalOffset,

        driveStartX,
        frontBoundary,
        garageElevation + roadVerticalOffset,
      );

      const curbLeftRampFront = faceByThreePoints(
        driveStartX - sidewalkRampLength,
        frontBoundary - 6,
        garageElevation + curbHeight + roadVerticalOffset,

        driveStartX - sidewalkRampLength,
        frontBoundary - 6,
        garageElevation + roadVerticalOffset,

        driveStartX,
        frontBoundary - 6,
        garageElevation + roadVerticalOffset,
      );

      const curbRightRampFace = faceByFourPoints(
        driveEndX,
        frontBoundary,
        garageElevation + roadVerticalOffset,

        driveEndX,
        frontBoundary - 6,
        garageElevation + roadVerticalOffset,

        driveEndX + sidewalkRampLength,
        frontBoundary - 6,
        garageElevation + roadVerticalOffset + curbHeight,

        driveEndX + sidewalkRampLength,
        frontBoundary,
        garageElevation + curbHeight + roadVerticalOffset,
      );

      const curbRightRampFront = faceByThreePoints(
        driveEndX,
        frontBoundary - 6,
        garageElevation + roadVerticalOffset,

        driveEndX + sidewalkRampLength,
        frontBoundary - 6,
        garageElevation + roadVerticalOffset,

        driveEndX + sidewalkRampLength,
        frontBoundary - 6,
        garageElevation + curbHeight + roadVerticalOffset,
      );

      if (curbLeftRampFace) {
        ret.curbGeom.push({
          group: new GeomTagGroup<ExteriorMaterialOverride>().add(
            curbLeftRampFace,
            exteriorMaterialOverrideNew("driveway0"),
          ),
          pickHandle: { type: "SITE", id: ret.id },
        });
      }
      if (curbRightRampFace) {
        ret.curbGeom.push({
          group: new GeomTagGroup<ExteriorMaterialOverride>().add(
            curbRightRampFace,
            exteriorMaterialOverrideNew("driveway0"),
          ),
          pickHandle: { type: "SITE", id: ret.id },
        });
      }
      if (curbLeftRampFront) {
        ret.curbGeom.push({
          group: new GeomTagGroup<ExteriorMaterialOverride>().add(
            curbLeftRampFront,
            exteriorMaterialOverrideNew("driveway0"),
          ),
          pickHandle: { type: "SITE", id: ret.id },
        });
      }
      if (curbRightRampFront) {
        ret.curbGeom.push({
          group: new GeomTagGroup<ExteriorMaterialOverride>().add(
            curbRightRampFront,
            exteriorMaterialOverrideNew("driveway0"),
          ),
          pickHandle: { type: "SITE", id: ret.id },
        });
      }

      ret.curbGeom.push({
        group: new GeomTagGroup<ExteriorMaterialOverride>().add(curb1, exteriorMaterialOverrideNew("driveway0")),
        pickHandle: { type: "SITE", id: ret.id },
      });
      ret.curbGeom.push({
        group: new GeomTagGroup<ExteriorMaterialOverride>().add(curb2, exteriorMaterialOverrideNew("driveway0")),
        pickHandle: { type: "SITE", id: ret.id },
      });
      ret.curbGeom.push({
        group: new GeomTagGroup<ExteriorMaterialOverride>().add(curbBase, exteriorMaterialOverrideNew("driveway0")),
        pickHandle: { type: "SITE", id: ret.id },
      });

      // the opposite side of the street
      const oppCurb = polyLine2Move(curbProfile, -roadWidth, 0);
      polyLine2ReflectOverYAxis(oppCurb);
      polyLine2SetCW(oppCurb);
      const oppCurbGeom = extrudeFaceAlongPolyLine3([oppCurb], sidewalkRailFull, 0, 0, 1);
      ret.sidewalkGeom.push({
        group: new GeomTagGroup<ExteriorMaterialOverride>().add(oppCurbGeom, exteriorMaterialOverrideNew("driveway0")),
        pickHandle: { type: "SITE", id: ret.id },
      });
    }

    if (sidewalk) {
      const sidewalkGeometry = makeSidewalk({
        sidewalkRail1,
        sidewalkRail2,
        sidewalkRailFull,
        driveStartX,
        driveEndX,
        frontBoundary,
        grassCurbWidth,
        sidewalkWidth,
        curbHeight,
        garageElevation,
        roadVerticalOffset,
        sidewalkRampLength,
        roadWidth,
      });
      ret.sidewalkGeom.push(...sidewalkGeometry.sidewalkGeom);
      ret.grassGeom.push(...sidewalkGeometry.grassGeom);
    }
  }

  siteTrees(contextExtension, ret, topoSrf, frontBoundary);

  if (fence && !sideFacingGarage) {
    const fenceOutline = polyLine3New([
      houseLeft,
      houseFront + fenceSetback,
      getSiteZ(topoSrf, houseLeft, houseFront + fenceSetback),

      siteBoundaryLeft,
      houseFront + fenceSetback,
      getSiteZ(topoSrf, siteBoundaryLeft, houseFront + fenceSetback),

      siteBoundaryLeft,
      houseBack,
      getSiteZ(topoSrf, siteBoundaryLeft, siteBack),

      siteBoundaryLeft,
      siteBack,
      getSiteZ(topoSrf, siteBoundaryLeft, siteBack),

      siteBoundaryRight,
      siteBack,
      getSiteZ(topoSrf, siteBoundaryRight, siteBack),

      siteBoundaryRight,
      houseBack,
      getSiteZ(topoSrf, siteBoundaryRight, siteBack),

      siteBoundaryRight,
      houseFront + fenceSetback,
      getSiteZ(topoSrf, siteBoundaryRight, houseFront + fenceSetback),

      houseRight,
      houseFront + fenceSetback,
      getSiteZ(topoSrf, houseRight, houseFront + fenceSetback),
    ]);

    ret.setbackDatum.push(fenceOutline);

    const fenceEdges = polyLine3Shatter(fenceOutline);

    makeFence(ret, fenceEdges, fenceHeight);
  }

  if (grassCurbWidth > 30) {
    // place some trees in the grass curb area
    ret.propPlacements.push(
      {
        ...sitePropDefaultValues,
        x: siteBoundaryLeft,
        y: frontBoundary + grassCurbWidth / 2,
        z: getSiteZ(topoSrf, siteBoundaryLeft + 86, frontBoundary + grassCurbWidth / 2),
      },
      {
        ...sitePropDefaultValues,
        x: siteBoundaryRight + sideSetback / 2,
        y: frontBoundary + grassCurbWidth / 2,
        z: getSiteZ(topoSrf, siteBoundaryLeft - 120, frontBoundary + grassCurbWidth / 2),
      },
    );
  }

  ret.setbackDatum.push(leftTopoRail);

  ret.propPlacements.push(
    {
      ...sitePropDefaultValues,
      x: houseLeft - 200,
      y: houseBack + 300,
      z: houseLowPoint,
    },
    {
      ...sitePropDefaultValues,
      x: houseRight + 300,
      y: houseBack + 500,
      z: houseLowPoint,
    },
  );

  let homePropName = "NeighborHouses_Proxy_01";
  let spacing = 300;

  switch (contextHomes) {
    case ContextHomeProp.DEFAULT:
      homePropName = "NeighborHouses_Proxy_01";
      spacing = 300;
      break;
    case ContextHomeProp.SMALL_RANCH:
      homePropName = "NeighborHouses_SmallRanch_01";
      spacing = 240;
      break;
    case ContextHomeProp.MEDIUM_RANCH:
      homePropName = "NeighborHouses_MediumRanch_01";
      spacing = 240;
      break;
    case ContextHomeProp.LARGE_RANCH:
      homePropName = "NeighborHouses_LargeRanch_01";
      spacing = 360;
      break;
    case ContextHomeProp.THREE_STORY:
      homePropName = "NeighborHouses_ThreeStory_01";
      spacing = 204;
      break;
    case ContextHomeProp.TWO_STORY:
      homePropName = "NeighborHouses_TwoStory_01";
      spacing = 320;
      break;
    default:
      homePropName = "NeighborHouses_Proxy_01";
      spacing = 300;
      break;
  }

  if (contextHomes !== ContextHomeProp.NONE) {
    ret.propPlacements.push(
      {
        ...sitePropDefaultValues,
        propName: homePropName,
        x: siteBoundaryLeft - sideSetback - spacing,
        y: frontBoundary + frontSetback,
        z: garageElevation,
      },
      {
        ...sitePropDefaultValues,
        propName: homePropName,
        x: siteBoundaryRight + sideSetback + spacing,
        y: frontBoundary + frontSetback,
        z: garageElevation,
      },
    );
  }

  return {
    ...ret,
  };
}

function siteTrees(contextExtension: number, ret: GeneratedSite, topoSrf: SiteZGeom, frontBoundary: number) {
  // add some trees to the perimeter
  const backOffset = 200;
  const midOffset = 600;
  const contextBuffer = 360;
  const backTreeZOffset = 120;
  const backTreePlacements = 25;

  for (
    let i = -contextExtension + contextBuffer;
    i < contextExtension - contextBuffer;
    i += contextExtension / (backTreePlacements / 2)
  ) {
    ret.propPlacements.push({
      ...sitePropDefaultValues,
      x: i,
      y: contextExtension - backOffset,
      z: getSiteZ(topoSrf, i, contextExtension - contextBuffer) - backTreeZOffset,
    });
  }
  const midTreePlacements = 12;
  for (
    let i = -contextExtension + contextBuffer;
    i < contextExtension - contextBuffer;
    i += contextExtension / (midTreePlacements / 2)
  ) {
    ret.propPlacements.push({
      ...sitePropDefaultValues,
      x: i,
      y: contextExtension - midOffset,
      z: getSiteZ(topoSrf, i, contextExtension),
    });
  }

  const sideMidPlacements = 7;
  const frontBuffer = 180;

  for (
    let i = frontBoundary + frontBuffer;
    i < contextExtension - contextBuffer;
    i += contextExtension / sideMidPlacements
  ) {
    ret.propPlacements.push(
      // right side
      {
        ...sitePropDefaultValues,
        x: contextExtension - midOffset,
        y: i,
        z: getSiteZ(topoSrf, contextExtension - midOffset, i),
      },
      // left side
      {
        ...sitePropDefaultValues,
        x: -contextExtension + midOffset,
        y: i,
        z: getSiteZ(topoSrf, -contextExtension + midOffset, i),
      },
    );
  }

  const sideBackPlacements = 12;
  for (
    let i = frontBoundary + frontBuffer;
    i < contextExtension - contextBuffer;
    i += contextExtension / sideBackPlacements
  ) {
    ret.propPlacements.push(
      {
        ...sitePropDefaultValues,
        x: contextExtension - backOffset,
        y: i,
        z: getSiteZ(topoSrf, contextExtension - backOffset, i) - backTreeZOffset,
      },
      {
        ...sitePropDefaultValues,
        x: -contextExtension + backOffset,
        y: i,
        z: getSiteZ(topoSrf, -contextExtension + backOffset, i) - backTreeZOffset,
      },
    );
  }
}
